import {
  CreateCcDataResult,
  IAssemblyComponent,
  IAssemblyScreen,
  IScreens,
  ISection,
} from 'gcf-player/shared-utils';
import { get, isEmpty, pick, toString } from 'lodash';
import { IOrientation } from 'src/constants/constants';
import { IComponent } from 'src/interfaces';

import {
  ComponentInstanceData,
  EComponentInstanceDataTarget,
  IMinScreenSize,
  SelectedSection,
} from './GcfPreview.interface';
import { BORDER_SIZE } from './GcfPreview.styled';

const DEFAULT_ZOOM_VALUE = 0.1;

export const isMatchingSection = (
  section: ISection,
  currentScreenSequence: number | string,
  criteria: {
    id: number | string;
    position?: number | string;
    screenSequence: number | string;
  },
) => {
  const isSameSection =
    section.id === criteria.id || section.uid === toString(criteria.id);
  const isSamePosition =
    toString(section.config?.position) === toString(criteria.position);
  const isSameScreenSequence =
    toString(currentScreenSequence) === toString(criteria.screenSequence);

  if (!!section.config?.position) {
    return isSameSection && isSamePosition && isSameScreenSequence;
  }

  return isSameSection && isSameScreenSequence;
};

export const getSectionComponentMetadata = (
  section: ISection,
  previewData?: CreateCcDataResult,
) => {
  const scheduledComponent = section?.components?.find(
    (item) => !item?.backupOrder,
  );

  const componentData = get(
    previewData,
    `ccData.components.${scheduledComponent?.componentId}`,
  ) as IComponent | undefined;

  const allowOverride = !!scheduledComponent?.allowOverrides;

  return {
    componentData,
    allowOverride,
  };
};

export const getFilteredScreenByPosition = (
  screen: IAssemblyScreen,
  activePosition: number | string,
) => {
  const hasPosition = screen.sections.some((item) =>
    get(item, 'config.position'),
  );

  if (!hasPosition) {
    return screen;
  }

  return {
    ...screen,
    sections: screen?.sections?.filter(
      (section: ISection) =>
        section?.config?.position === Number(activePosition),
    ),
  };
};

export const getScreenPositions = (screen: IAssemblyScreen) => {
  const positions = screen.sections
    .map((section) => section?.config?.position)
    .filter((position): position is number => typeof position === 'number');

  return Array.from(new Set(positions)).sort((a, b) => a - b);
};

export const filterOutDynamicSection = (sections: ISection[]) =>
  sections?.filter(
    (section: ISection) =>
      section?.config?.dynamicHighlight === 0 ||
      !section?.config?.dynamicHighlight,
  );

export const getActiveScreenFromScreens = (
  activeScreen: string | number,
  activeAssemblyScreens?: Record<string, IAssemblyScreen>,
) => {
  if (!activeAssemblyScreens || isEmpty(activeAssemblyScreens)) {
    return {};
  }

  if (activeScreen === 'all') {
    return activeAssemblyScreens;
  }

  const res = pick(activeAssemblyScreens, String(activeScreen));

  if (!isEmpty(res)) {
    return res;
  }

  // return a fallback value
  return activeAssemblyScreens;
};

const areScreenIdsValid = (assemblyScreensIds: number[], screens: IScreens) =>
  assemblyScreensIds.every(
    (screenId) =>
      screens[screenId] && screens[screenId].width && screens[screenId].height,
  );

const getAssemblySize = (assemblyScreensIds: number[], screens: IScreens) => {
  let assemblyWidth = 0;
  let assemblyHeight = 0;

  for (const id of assemblyScreensIds) {
    const screen = screens[id];

    assemblyWidth += screen.width;

    if (assemblyHeight === 0) {
      assemblyHeight += screen.height;
    }
  }

  return {
    assemblyWidth,
    assemblyHeight,
  };
};

export const getZoomValueWithoutMinScreenSize = (
  containerSize: {
    width: number;
    height: number;
  },
  activeAssemblyScreens: Record<string, IAssemblyScreen>,
  screens: IScreens,
  fallbackValue = DEFAULT_ZOOM_VALUE,
) => {
  const assemblyScreensIds = Object.values(activeAssemblyScreens).map(
    (item) => item.screenId,
  );

  if (!areScreenIdsValid(assemblyScreensIds, screens)) {
    return fallbackValue;
  }

  const { assemblyWidth, assemblyHeight } = getAssemblySize(
    assemblyScreensIds,
    screens,
  );

  if (
    containerSize.width !== 0 &&
    containerSize.height !== 0 &&
    assemblyWidth !== 0 &&
    assemblyHeight !== 0
  ) {
    const minScale = Math.min(
      containerSize.width /
        (assemblyWidth + BORDER_SIZE * (assemblyScreensIds.length + 1)),
      containerSize.height / (assemblyHeight + BORDER_SIZE * 2),
    );

    return Math.floor(minScale * 1000) / 1000;
  }

  return fallbackValue;
};

const getMinAssemblySize = (
  screenLength: number,
  minScreenSize: IMinScreenSize,
  isVertical: boolean,
) => {
  const direction = isVertical
    ? IOrientation.VERTICAL
    : IOrientation.HORIZONTAL;

  const minAssemblyWidth = minScreenSize[direction].width * screenLength;

  const minAssemblyHeight = minScreenSize[direction].height;

  return {
    minAssemblyWidth,
    minAssemblyHeight,
  };
};

const isAssemblySizeGreaterThanContainer = (
  containerSize: {
    width: number;
    height: number;
  },
  minAssemblyWidth: number,
  minAssemblyHeight: number,
) => {
  return (
    minAssemblyWidth > containerSize.width ||
    minAssemblyHeight > containerSize.height
  );
};

export const getZoomValueWithMinScreenSize = (
  containerSize: {
    width: number;
    height: number;
  },
  activeAssemblyScreens: Record<string, IAssemblyScreen>,
  screens: IScreens,
  minScreenSize: IMinScreenSize,
  fallbackValue = DEFAULT_ZOOM_VALUE,
) => {
  const screenLength = Object.keys(activeAssemblyScreens).length;
  const screenInfo = screens[Object.values(activeAssemblyScreens)[0].screenId];

  if (!screenInfo) {
    return fallbackValue;
  }

  const isVertical = screenInfo.height > screenInfo.width;
  const assemblyScreensIds = Object.values(activeAssemblyScreens).map(
    (item) => item.screenId,
  );
  const { minAssemblyWidth, minAssemblyHeight } = getMinAssemblySize(
    screenLength,
    minScreenSize,
    isVertical,
  );
  const { assemblyWidth, assemblyHeight } = getAssemblySize(
    assemblyScreensIds,
    screens,
  );

  if (
    isAssemblySizeGreaterThanContainer(
      containerSize,
      minAssemblyWidth,
      minAssemblyHeight,
    )
  ) {
    const maxScale = Math.max(
      minAssemblyWidth / assemblyWidth,
      minAssemblyHeight / assemblyHeight,
    );

    return Math.floor(maxScale * 1000) / 1000;
  }

  return getZoomValueWithoutMinScreenSize(
    containerSize,
    activeAssemblyScreens,
    screens,
  );
};

export const getZoomValueByContainerSize = (
  containerSize: {
    width: number;
    height: number;
  },
  activeAssemblyScreens?: Record<string, IAssemblyScreen>,
  screens?: IScreens,
  minScreenSize?: IMinScreenSize,
) => {
  if (isEmpty(activeAssemblyScreens) || isEmpty(screens))
    return DEFAULT_ZOOM_VALUE;

  if (minScreenSize) {
    return getZoomValueWithMinScreenSize(
      containerSize,
      activeAssemblyScreens,
      screens,
      minScreenSize,
    );
  }

  return getZoomValueWithoutMinScreenSize(
    containerSize,
    activeAssemblyScreens,
    screens,
  );
};

export const getComponentInstances = (
  params: {
    screenSequence: number;
    position?: number;
    id?: number;
    componentId: number;
    isScheduledItem: boolean;
    parent?: unknown;
  },
  previewData?: CreateCcDataResult,
): ComponentInstanceData => {
  const { position, screenSequence, id, isScheduledItem, parent } = params;

  const activeAssemblyScreens = get(previewData, 'activeAssembly.screens');
  const currentScreen = get(activeAssemblyScreens, `${screenSequence}`);
  const currentSection = get(currentScreen, 'sections', []).filter(
    (item: ISection) =>
      isMatchingSection(item, screenSequence, {
        id: id || '',
        position: position || '',
        screenSequence,
      }),
  )?.[0];

  const { ref, backups } = get(currentSection, 'components', []).reduce(
    (acc: any, item: IAssemblyComponent) => {
      if (!item.backupOrder) {
        acc.ref = item;
      } else {
        acc.backups.push(item);
      }

      return acc;
    },
    { ref: null, backups: [] },
  );

  return {
    target: parent
      ? EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE
      : isScheduledItem
      ? EComponentInstanceDataTarget.SCHEDULE_ITEM
      : EComponentInstanceDataTarget.ASSEMBLY,
    section: currentSection,
    ref,
    backups,
    parent,
    screenSequence,
  };
};

export const getSectionDimmedState = (params: {
  section: ISection;
  screenSequence: number | string;
  selectedSection?: SelectedSection;
  allowOverride: boolean;
  highlightAll?: boolean;
}) => {
  const {
    section,
    screenSequence,
    selectedSection,
    allowOverride,
    highlightAll,
  } = params;

  // Case 1: User Clicked Section
  if (selectedSection) {
    const isSelected = isMatchingSection(
      section,
      screenSequence,
      selectedSection,
    );

    return !isSelected; // Dim everyone else
  }

  // Case 2: Global Toggle ON (Show all)
  if (highlightAll) {
    return false;
  }

  // Case 3: Override Mode (highlightAll is false)
  return !allowOverride;
};

export const getScreenSxValue = (isVisibleScreen: boolean) => {
  return isVisibleScreen
    ? {}
    : {
        position: 'absolute',
        display: 'none',
      };
};

export const isExpandScreenButtonVisible = (
  onScreenLabelClick: Function | undefined,
  isSingleScreenView: boolean,
) => {
  return !!onScreenLabelClick && !isSingleScreenView;
};
